import React, { useEffect, useRef, useState } from "react";
import { dialog } from "../../components/ui/Dialog";
import { PROC_STATUS, procCur, procEsc, procNorm } from "./procHelpers";
import { procCan, procNormId, procPeople } from "./procPeople";
import { Icon, PrIco } from "./ProcUi";

// Éditeur de page — repris du prototype (procedures-lib.jsx).
// Différences : les confirm()/prompt() natifs passent par la boîte de dialogue
// de l'application, et les responsables viennent des vrais comptes.

const ProcLinkPicker = ({ pages, db, onPick, onClose }) => {
  const [q, setQ] = useState("");
  const [i, setI] = useState(0);
  const nq = procNorm(q.trim());
  const list = pages
    .filter((p) => !nq || procNorm(procCur(p).title).includes(nq) || p.tags.some((t) => procNorm(t).includes(nq)))
    .slice(0, 8);
  const sp = (id) => (db.spaces.find((s) => s.id === id) || {}).name;
  return (
    <div className="pr-lp" onMouseDown={(e) => e.stopPropagation()}>
      <div className="pr-lp-in">
        <PrIco n="xref" s={15} />
        <input
          autoFocus
          value={q}
          placeholder="Rechercher la procédure à lier…"
          onChange={(e) => { setQ(e.target.value); setI(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setI((x) => Math.min(list.length - 1, x + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setI((x) => Math.max(0, x - 1)); }
            else if (e.key === "Enter") { e.preventDefault(); if (list[i]) onPick(list[i]); }
            else if (e.key === "Escape") { e.preventDefault(); onClose(); }
          }}
        />
        <button onClick={onClose} title="Fermer"><Icon name="x" size={13} /></button>
      </div>
      <div className="pr-lp-list">
        {list.map((p, k) => (
          <button key={p.id} className={k === i ? "on" : ""} onMouseEnter={() => setI(k)} onClick={() => onPick(p)}>
            <span className="pr-lp-t">{procCur(p).title}</span>
            <span className="pr-lp-s">{sp(p.spaceId)}</span>
          </button>
        ))}
        {!list.length && <div className="pr-empty">Aucune procédure trouvée.</div>}
      </div>
      <div className="pr-lp-hint">↑ ↓ pour naviguer · Entrée pour insérer · Échap pour fermer</div>
    </div>
  );
};

const ProcEditor = ({ page, db, isNew, onCancel, onSave }) => {
  const cur = procCur(page);
  const ref = useRef(null);
  const [title, setTitle] = useState(cur.title);
  const [summary, setSummary] = useState("");
  const [meta, setMeta] = useState({
    status: page.status, owner: page.owner, reviewDate: page.reviewDate, tags: page.tags,
    spaceId: page.spaceId, sectionId: page.sectionId,
  });
  // Texte brut du champ mots-clés. Dans le prototype la valeur était recalculée
  // à chaque frappe (join), ce qui avalait la virgule tapée : impossible de
  // saisir plusieurs mots-clés à la main. On garde donc le texte tel que saisi
  // et on n'en déduit la liste qu'à côté.
  const [tagsText, setTagsText] = useState(page.tags.join(", "));
  const [dirty, setDirty] = useState(false);
  const [blk, setBlk] = useState("p");
  const [picker, setPicker] = useState(null);
  const rangeRef = useRef(null);

  // Contenu injecté UNE fois à l'ouverture : le recharger à chaque rendu
  // écraserait la saisie en cours dans le champ éditable.
  useEffect(() => {
    ref.current.innerHTML = cur.html;
    if (isNew) setTimeout(() => ref.current && ref.current.focus(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkable = db.pages.filter((p) => !p.trashed && p.id !== page.id && procCan(db, p, "read"));

  const saveRange = () => {
    const s = window.getSelection();
    rangeRef.current = s.rangeCount && ref.current.contains(s.anchorNode) ? s.getRangeAt(0).cloneRange() : null;
  };
  const openPicker = (fromBrackets) => {
    saveRange();
    setPicker({ fromBrackets, text: !fromBrackets && rangeRef.current ? rangeRef.current.toString().trim() : "" });
  };
  const restore = () => {
    ref.current.focus();
    const s = window.getSelection();
    s.removeAllRanges();
    if (rangeRef.current) s.addRange(rangeRef.current);
    else {
      const r = document.createRange();
      r.selectNodeContents(ref.current);
      r.collapse(false);
      s.addRange(r);
    }
    return s;
  };

  const syncBlk = () => {
    try {
      const v = (document.queryCommandValue("formatBlock") || "p").toLowerCase();
      setBlk(["h2", "h3"].includes(v) ? v : "p");
    } catch (e) {
      // queryCommandValue peut échouer hors sélection : sans conséquence
    }
  };
  const exec = (cmd, val) => {
    ref.current.focus();
    document.execCommand(cmd, false, val);
    setDirty(true);
    syncBlk();
  };
  const ins = (html) => exec("insertHTML", html);

  const insertLink = (p) => {
    const s = restore();
    if (picker.fromBrackets && s.rangeCount) {
      const r = s.getRangeAt(0);
      const n = r.startContainer;
      if (n.nodeType === 3 && r.startOffset >= 2 && n.textContent.slice(r.startOffset - 2, r.startOffset) === "[[") {
        r.setStart(n, r.startOffset - 2);
        s.removeAllRanges();
        s.addRange(r);
      }
    }
    document.execCommand(
      "insertHTML", false,
      `<a class="pr-xref" data-page="${p.id}" href="#proc-${p.id}">${procEsc(picker.text || procCur(p).title)}</a>&nbsp;`
    );
    ref.current.querySelectorAll("a.pr-xref[style]").forEach((a) => a.removeAttribute("style"));
    setPicker(null);
    setDirty(true);
  };

  const onInput = () => {
    setDirty(true);
    const s = window.getSelection();
    if (!s.rangeCount) return;
    const r = s.getRangeAt(0);
    const n = r.startContainer;
    if (n.nodeType === 3 && r.startOffset >= 2 && n.textContent.slice(r.startOffset - 2, r.startOffset) === "[[") openPicker(true);
  };

  const setM = (k, v) => {
    setMeta((m) => ({ ...m, [k]: v }));
    setDirty(true);
  };
  const sections = db.sections.filter((s) => s.spaceId === meta.spaceId);

  const cancel = async () => {
    if (dirty) {
      const ok = await dialog.confirm("Abandonner les modifications non enregistrées ?", {
        variant: "warning", title: "Modifications non enregistrées", confirmLabel: "Abandonner", cancelLabel: "Continuer l'édition",
      });
      if (!ok) return;
    }
    onCancel();
  };

  const save = (status) => {
    const html = ref.current.innerHTML.replace(/<p><br><\/p>$/, "");
    onSave({
      title: title.trim() || "Sans titre",
      html,
      summary: summary.trim() || (isNew ? "Rédaction initiale" : ""),
      meta: { ...meta, status: status || meta.status },
    });
  };

  // Le lien web demande une adresse dans une boîte de dialogue, ce qui fait
  // perdre la sélection du texte : on la mémorise avant et on la rétablit après.
  const addWebLink = async () => {
    saveRange();
    const u = await dialog.prompt("Adresse du lien", "https://", { title: "Lien web", confirmLabel: "Insérer" });
    if (!u) return;
    restore();
    exec("createLink", u);
  };

  const tb = [
    { g: [
      { k: "bold", i: <b>B</b>, t: "Gras (Ctrl+B)", fn: () => exec("bold") },
      { k: "italic", i: <i>I</i>, t: "Italique (Ctrl+I)", fn: () => exec("italic") },
      { k: "u", i: <u>S</u>, t: "Souligné (Ctrl+U)", fn: () => exec("underline") },
    ] },
    { g: [
      { k: "ul", i: <PrIco n="ul" />, t: "Liste à puces", fn: () => exec("insertUnorderedList") },
      { k: "ol", i: <PrIco n="ol" />, t: "Liste numérotée (étapes)", fn: () => exec("insertOrderedList") },
      { k: "chk", i: <PrIco n="chk" />, t: "Liste de contrôle", fn: () => ins(`<ul class="check"><li>Point à vérifier</li></ul><p><br></p>`) },
    ] },
    { g: [
      { k: "info", i: <PrIco n="info" />, t: "Encadré information", fn: () => ins(`<div class="callout info"><p>Information</p></div><p><br></p>`) },
      { k: "warn", i: <PrIco n="warn" />, t: "Encadré vigilance", fn: () => ins(`<div class="callout warn"><p><strong>Attention :</strong> </p></div><p><br></p>`) },
      { k: "tbl", i: <PrIco n="tbl" />, t: "Tableau", fn: () => ins(`<table><tr><th>Colonne 1</th><th>Colonne 2</th></tr><tr><td></td><td></td></tr><tr><td></td><td></td></tr></table><p><br></p>`) },
      { k: "xref", i: <PrIco n="xref" />, t: "Lien vers une procédure (ou tapez [[ )", fn: () => openPicker(false) },
      { k: "link", i: <PrIco n="link" />, t: "Lien web", fn: addWebLink },
      { k: "hr", i: <PrIco n="hr" />, t: "Séparateur", fn: () => exec("insertHorizontalRule") },
    ] },
    { g: [
      { k: "undo", i: <PrIco n="undo" />, t: "Annuler (Ctrl+Z)", fn: () => exec("undo") },
      { k: "redo", i: <PrIco n="redo" />, t: "Rétablir", fn: () => exec("redo") },
      { k: "clr", i: <PrIco n="clr" />, t: "Effacer la mise en forme", fn: () => exec("removeFormat") },
    ] },
  ];

  const people = procPeople();

  return (
    <div className="pr-edit">
      <div className="pr-edit-main">
        <div className="pr-toolbar">
          <select className="pr-tb-sel" value={blk} onChange={(e) => exec("formatBlock", e.target.value)}>
            <option value="p">Texte</option>
            <option value="h2">Titre</option>
            <option value="h3">Sous-titre</option>
          </select>
          {tb.map((grp, gi) => (
            <div key={gi} className="pr-tb-g">
              {grp.g.map((b) => (
                <button key={b.k} className="pr-tb-btn" title={b.t} onMouseDown={(e) => { e.preventDefault(); b.fn(); }}>{b.i}</button>
              ))}
            </div>
          ))}
        </div>
        {picker && (
          <ProcLinkPicker pages={linkable} db={db} onPick={insertLink} onClose={() => { setPicker(null); restore(); }} />
        )}
        <div className="pr-edit-scroll">
          <div className="pr-edit-paper">
            <input
              className="pr-edit-title"
              value={title}
              placeholder="Titre de la page"
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            />
            <div
              ref={ref}
              className="pr-content pr-editable"
              contentEditable
              suppressContentEditableWarning
              onInput={onInput}
              onKeyUp={syncBlk}
              onMouseUp={syncBlk}
              onClick={(e) => { if (e.target.closest("a")) e.preventDefault(); }}
            ></div>
          </div>
        </div>
        <div className="pr-savebar">
          <input
            className="pr-sum"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Résumé de la modification (ex. : mise à jour des seuils 2026)"
            onKeyDown={(e) => e.key === "Enter" && save()}
          />
          <button className="btn" onClick={cancel}>Annuler</button>
          {meta.status !== "publie" && (
            <button className="btn" onClick={() => save()}>Enregistrer en {PROC_STATUS[meta.status].lab.toLowerCase()}</button>
          )}
          <button className="btn primary" onClick={() => save("publie")}>
            <Icon name="check" size={14} /> {page.status === "publie" ? "Enregistrer" : "Publier"}
          </button>
        </div>
      </div>
      <aside className="pr-rail">
        <div className="pr-rail-card">
          <div className="pr-rail-h">Informations</div>
          <label className="pr-f-lab">Statut</label>
          <div className="pr-seg">
            {Object.entries(PROC_STATUS).map(([k, s]) => (
              <button key={k} className={meta.status === k ? "on tone-" + s.tone : ""} onClick={() => setM("status", k)}>{s.lab}</button>
            ))}
          </div>
          <label className="pr-f-lab">Espace</label>
          <select
            className="pr-f-in"
            value={meta.spaceId}
            onChange={(e) => {
              const sp = e.target.value;
              setMeta((m) => ({ ...m, spaceId: sp, sectionId: (db.sections.find((s) => s.spaceId === sp) || {}).id }));
              setDirty(true);
            }}
          >
            {db.spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label className="pr-f-lab">Section</label>
          <select className="pr-f-in" value={meta.sectionId} onChange={(e) => setM("sectionId", e.target.value)}>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label className="pr-f-lab">Responsable</label>
          <select className="pr-f-in" value={procNormId(meta.owner)} onChange={(e) => setM("owner", e.target.value)}>
            {people.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label className="pr-f-lab">Prochaine révision</label>
          <input
            type="date"
            className="pr-f-in"
            value={meta.reviewDate ? new Date(meta.reviewDate).toISOString().slice(0, 10) : ""}
            onChange={(e) => setM("reviewDate", e.target.value ? new Date(e.target.value).getTime() : null)}
          />
          <label className="pr-f-lab">Mots-clés</label>
          <input
            className="pr-f-in"
            value={tagsText}
            placeholder="bilan, TVA…"
            onChange={(e) => {
              setTagsText(e.target.value);
              setM("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean));
            }}
          />
        </div>
        <div className="pr-rail-note">
          <Icon name="info" size={14} /> Chaque enregistrement crée une nouvelle version (v{cur.v + 1}). Les versions précédentes restent consultables et restaurables.
        </div>
      </aside>
    </div>
  );
};

export default ProcEditor;
